import _ from 'lodash';
import { Fragment, useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";

import { getDateFormatted, getMinDateLimitBehaviour } from 'Core/data/Helpers';
import { Typography, Chip, Grid } from '@mui/material';
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import Icon from '@mui/material/Icon';
import { Button, Tooltip, Select } from "@gpstrackit/react-controls";
import RawMessageView from 'Modules/devices/.components/rawMessageView'
import Cancel from '@mui/icons-material/Cancel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import InputDate from 'Components/InputDate';
import moment from 'moment';
import Filters from 'Components/Filters';

//Notification
import { NotificationManager } from "Components/Notifications";

//Redux
import {
    rawMessageViewOpenModal,
    activitiesSearchUsersTabRequest,
    searchEntityRequest,
    clearRedux
} from "Redux/actions";

const RANGE_MODE_ROWS = 50;

const UserTroubleshooting = (props) => {
    const { timezone, tab, height, device, className, companyName } = props;
    const tableRef = useRef();
    const { messages } = useIntl();
    const dispatch = useDispatch();

    const rawDataFilters = useSelector(state => state.rawDataTabRedux.filters);

    const dateFormat = () => {
        return `
            let dateToformat = moment(row?.createdOn).subtract(5, 'hours');
            return dateToformat.format("MMM DD YYYY h:mm:ss a");
        `
    }

    //Redux states
    const open = useSelector(state => state.rawMessageViewRedux.openModal)
    const pmUsers = useSelector(state => state.generalRedux.pmUsers);
    const { loading, nextToken, users } = useSelector(state => state.activitiesTabRedux);

    //Local States
    const current_week = moment().startOf('week');
    const tempStartDate = new Date(current_week.toString());
    const [startDate, setStartDate] = useState(rawDataFilters?.startDate ||tempStartDate);
    const [endDate, setEndDate] = useState(rawDataFilters?.endDate || new Date());
    const [beforeRangeDate, setBeforeRangeDate] = useState({
        start: startDate,
        end: endDate
    })
    const [createdBy, setCreatedby] = useState(null);
    const [filters, setFilters] = useState({
        startDate: startDate,
        endDate: endDate,
        page: 1,
        rowsPeerPage: 20,
        filterByDate: true,
        limit: 10
    })

    const [last, setLast] = useState(rawDataFilters?.last ||{ id: '20', name: '20' });
    const [type, setType] = useState(rawDataFilters?.type || 'last');



    const [columns, setColumns] = useState([
        {
            key: 'maintenance-warning-view',
            show: true,
            locked: true,
            title: "",
            width: 40,
            icon: true,
            render: (item) => {
                return (
                    <Tooltip title="View Detail">
                        <Icon style={{ cursor: "pointer" }} className="toolbar-list-icon fs-20" onClick={() => {
                            dispatch(rawMessageViewOpenModal(true, item?.flow ? JSON.parse(item?.flow) : "", messages["user_troubleshooting_tab_details"]))
                        }}>
                            remove_red_eye
                        </Icon>
                    </Tooltip>

                )
            }
        },
        {
            key: 'createdBy',
            field: 'createdBy',
            title: messages['activities_tab_created_by'],
            width: 200,
            show: true,
            resize: true,
            export: true,
            functionString: `
                if(row?.createdBy?.startsWith("PM")){
                    let pmUsers = ${JSON.stringify(pmUsers)}
                    pmUsers = pmUsers.find((item) => item.username === row?.username)
                    return pmUsers.firstName + " " + pmUsers.lastName || row?.username
                } else {
                    let pxUsers = ${JSON.stringify(users)}
                    let regexSubUser = /^PX_\d+_\d+$/;
                    if (regexSubUser.test(row?.createdBy)) {
                        let id = row?.createdBy.split("_")[2];
                        let user = pxUsers.find((item) => (item?.id == +id))
                        return user?.userName
                    } else {
                        return ${companyName ? JSON.stringify(companyName) : ""}
                    }
                }
            `,
            code: "activities_tab_created_by",
            render: (item) => {
                return <span>{item?.createdBy && item?.createdBy?.startsWith("PM") ? getPMUser(item?.createdBy.split("_")[1], pmUsers) : accountValidation(item?.createdBy, users)}</span>
            }
        },
        {
            key: 'createdOn',
            field: 'createdOn',
            show: true,
            locked: false,
            title: messages["user_troubleshooting_tab_created_on"],
            width: 200,
            export: true,
            functionString: dateFormat(),
            code: "user_troubleshooting_tab_createdOn",
            render: (dataItem) => {
                return (
                    <Fragment>
                        {
                            dataItem.createdOn &&
                            getDateFormatted(dataItem.createdOn, "date", "date", null, timezone)
                        }
                    </Fragment>
                )
            }
        },
        {
            key: 'installFlag',
            show: true,
            locked: false,
            title: messages["user_troubleshooting_installed"],
            width: 200,
            export: true,
            functionString: `function installFormat(flow) {
                let flowData =flow ? JSON.parse(flow) : null;
                let installed = flowData?.steps?.find(x => x.step === "Connection History - Installation History") || null
                let passed = installed?.passed || false

                return passed ? ${messages["user_troubleshooting_tab_passed"]} : ${messages["user_troubleshooting_tab_fail"]};
            }

            return row?.flow ? installFormat(row?.flow) : ''
            `,
            code: "user_troubleshooting_installed",
            render: (dataItem) => {
                let flowData = dataItem?.flow ? JSON.parse(dataItem.flow) : null
                let installed = flowData?.steps?.find(x => x.step === "Connection History - Installation History") || null
                let passed = installed?.passed || false
                return (
                    <div>
                        {
                            installed && passed &&
                            <CheckCircle style={{ color: 'green' }} />
                        }
                        {
                            installed && !passed &&
                            <Cancel style={{ color: 'red' }} />
                        }
                    </div>
                )
            }
        },
        {
            key: 'simStatus',
            show: true,
            locked: false,
            title: messages["user_troubleshooting_sim_status"],
            width: 200,
            export: true,
            functionString: `function simStatus(flow) {
                let flowData =flow ? JSON.parse(flow) : null;
                let simStatus = flowData?.steps?.find(x => x.step === "Connection History - Cellular History") || null
                let passed = simStatus?.passed || false

                return passed ? ${messages["user_troubleshooting_tab_passed"]} : ${messages["user_troubleshooting_tab_fail"]};
            }

            return row?.flow ? simStatus(row?.flow) : ''
            `,
            code: "user_troubleshooting_sim_status",
            render: (dataItem) => {
                let flowData = dataItem?.flow ? JSON.parse(dataItem.flow) : null
                let simStatus = flowData?.steps?.find(x => x.step === "Connection History - Cellular History") || null
                let passed = simStatus?.passed || false
                return (
                    <div>
                        {
                            simStatus && passed &&
                            <CheckCircle style={{ color: 'green' }} />
                        }
                        {
                            simStatus && !passed &&
                            <Cancel style={{ color: 'red' }} />
                        }
                    </div>
                )
            }
        },
        {
            key: 'gps',
            show: true,
            locked: false,
            title: messages["user_troubleshooting_gps"],
            width: 200,
            export: true,
            functionString: `function simStatus(flow) {
                let flowData = flow ? JSON.parse(flow) : null;
                let locationSteps = flowData?.steps?.filter(x => x.step === "Location") || []
                let passed = locationSteps?.every(x => x.passed === true) || false

                return passed ? ${messages["user_troubleshooting_tab_passed"]} : ${messages["user_troubleshooting_tab_fail"]};
            }

            return row?.flow ? simStatus(row?.flow) : ''
            `,
            code: "user_troubleshooting_gps",
            render: (dataItem) => {
                let flowData = dataItem?.flow ? JSON.parse(dataItem.flow) : null
                let locationSteps = flowData?.steps?.filter(x => x.step === "Location") || []
                let passed = locationSteps?.every(x => x.passed === true) || false

                return (
                    <div>
                        {
                            locationSteps.length > 0 && passed &&
                            <CheckCircle style={{ color: 'green' }} />
                        }
                        {
                            locationSteps.length > 0 && !passed &&
                            <Cancel style={{ color: 'red' }} />
                        }
                    </div>
                )
            }
        }
        ,
        {
            key: 'odoEngine',
            show: true,
            locked: false,
            title: messages["user_troubleshooting_odo_engine"],
            width: 200,
            export: true,
            functionString: `function simStatus(flow) {
                let flowData = flow ? JSON.parse(flow) : null;
                let odoEngine = flowData?.steps?.find(x => x.step === "Odometer and Engine Hours") || null
                let passed = odoEngine?.passed || false

                return passed ? ${messages["user_troubleshooting_tab_passed"]} : ${messages["user_troubleshooting_tab_fail"]};
            }

            return row?.flow ? simStatus(row?.flow) : ''
            `,
            code: "user_troubleshooting_odo_engine",
            render: (dataItem) => {
                let flowData = dataItem?.flow ? JSON.parse(dataItem.flow) : null
                let odoEngine = flowData?.steps?.find(x => x.step === "Odometer and Engine Hours") || null
                let passed = odoEngine?.passed || false
                return (
                    <div>
                        {
                            odoEngine && passed &&
                            <CheckCircle style={{ color: 'green' }} />
                        }
                        {
                            odoEngine && !passed &&
                            <Cancel style={{ color: 'red' }} />
                        }
                    </div>
                )
            }
        }
    ])

    const onChangeStartEndDate = ({ start, end }) => {
        const newStart = start && new Date(start);
        const newEnd = end && new Date(end);

        let fltrs = filters;
        fltrs.startDate = newStart;
        fltrs.endDate = newEnd;
        // Set New Date Range
        setStartDate(newStart);
        setEndDate(newEnd);
        if (start && end) {
            const newDateRange = {
                start,
                end,
            };
            setBeforeRangeDate(newDateRange);
        }
        setFilters({ ...fltrs });
    }

    const onCloseSelectDate = () => {
        const { start, end } = beforeRangeDate;
        if (start && end && !endDate) {
            let fltrs = filters;
            fltrs.startDate = start;
            fltrs.endDate = end;
            setFilters({ ...fltrs });
            setStartDate(start);
            setEndDate(end);
        }
    }

    const onChange = (value) => {
        const { start, end } = value;
        onChangeStartEndDate({
            start,
            end
        })
    };

    const accountValidation = (name, list) => {
        let createdBy = name
        let regexSubUser = /^PX_\d+_\d+$/;
        if (name.includes("PX")) {
            if (regexSubUser.test(name)) {
                try {
                    let id = name.split("_")[2];
                    let user = list.find((item) => (item?.id == +id))
                    createdBy = user?.userName
                } catch (error) { }
            } else {
                createdBy = companyName
            }
        }
        return createdBy || name
    }

    const usersHandler = (item) => {

        //activityHandler(null, null, null, item?.userName, !item ? true : false);

        //save preferences
        let currentConditions = tableRef?.current?.conditions;

        //Delete
        let deleteUsername = currentConditions.findIndex((item) => item.fields[0] === "createdBy");
        deleteUsername >= 0 && currentConditions.splice(deleteUsername, 1);

        if (item)
            currentConditions.push({ fields: ["createdBy"], terms: [item.id], conditionOperator: "AND" })

        setCreatedby(item?.id || null);

        tableRef?.current?.getData({ newConditions: currentConditions });

    }

    const applyFilters = (paramType) => {
        const currentType = paramType ?? type;
        if (currentType !== "last" && (!startDate || !endDate)) {
            NotificationManager.warning(
                messages['user_troubleshooting_tab_date_range'],
                messages['user_troubleshooting_tab_title'],
                5000,
            );

            return
        }

        const DEFAULT_ROWS = 50;
        
        // Build conditions fresh - preserve only search tags
        let currentConditions = tableRef?.current?.conditions || [];
        let searchTags = currentConditions.find((item) => item?.tags);
        let newConditions = searchTags ? [searchTags] : [];

        // Condition 1: ID filter (always needed)
        newConditions.push({
            fields: ["unitId"], terms: [device.id], exact: true,
            conditionOperator: "AND",
        });

        // Condition 2: Range filter (SEPARATE - only in range mode)
        if (currentType !== "last") {
            newConditions.push({
                range: {
                    fields: ["createdOn"],
                    GTE: moment(moment(filters.startDate).format("YYYY-MM-DD") + " 00:00:00").utc().format("YYYY-MM-DDTHH:mm:ss.sss") + "Z",
                    LTE: moment(moment(filters.endDate).format("YYYY-MM-DD") + " 23:59:00").utc().format("YYYY-MM-DDTHH:mm:ss.sss") + "Z"
                },
                conditionOperator: "AND"
            });
        }

        // Condition 3: CreatedBy filter (if selected)
        if (createdBy) {
            newConditions.push({
                fields: ["createdBy"], terms: [createdBy.id], exact: true,
                conditionOperator: "AND"
            });
        }

        tableRef?.current?.getData({ newRows: currentType === "last" ? parseInt(last.id, 10) : DEFAULT_ROWS, newSort: currentType === 'last' ? [{ field: "createdOn", order: "DESC" }] : undefined, newConditions });
    }

    const handleChangeConditions = (tags, conditions, total, newColumns, rows, sort, key, fromPreferences, restoreOnly) => {
        const DEFAULT_ROWS = 50;
        
        let incomingConditions = conditions || [];
        let currentCreatedBy = createdBy
        let currentType = type
        
        // Detect range mode from incoming conditions (preferences restore)
        let currentDateType = incomingConditions.find((item) => item?.range?.fields);
        if (currentDateType) {
            // Extract dates from range condition
            const tz = timezone || moment.tz.guess();
            const restoredStart = new Date(moment.utc(currentDateType.range.GTE).tz(tz).format("YYYY-MM-DD") + "T00:00:00");
            const restoredEnd = new Date(moment.utc(currentDateType.range.LTE).tz(tz).format("YYYY-MM-DD") + "T00:00:00");
            setStartDate(restoredStart);
            setEndDate(restoredEnd);
            setFilters(prev => ({ ...prev, startDate: restoredStart, endDate: restoredEnd }));
            setBeforeRangeDate({ start: restoredStart, end: restoredEnd });
            setType('range');
            currentType = 'range';
        } else {
            setType('last');
            currentType = 'last';
        }

        // Enforce minimum rows in range mode for consistency
        const effectiveRows = currentType === 'last' 
            ? rows 
            : Math.max(rows || DEFAULT_ROWS, DEFAULT_ROWS);

        if (effectiveRows)
            setLast({ id: `${effectiveRows}`, name: `${effectiveRows}` })

        // Extract createdBy filter from incoming conditions
        let currentSelectedUser = incomingConditions.find((item) => item?.fields?.[0] === 'createdBy')
        if (currentSelectedUser) {
            setCreatedby(currentSelectedUser.terms[0])
        }

        // restoreOnly: data already persisted in redux, just sync UI states — skip getData
        if (restoreOnly) return;

        // Build conditions fresh - preserve only search tags
        let searchTags = incomingConditions.find((item) => item?.tags);
        let newConditions = searchTags ? [searchTags] : [];

        // Condition 1: ID filter (always needed)
        newConditions.push({
            fields: ["unitId"], terms: [device.id], exact: true,
            conditionOperator: "AND",
        });

        // Condition 2: Range filter (SEPARATE - only in range mode)
        // Use restored dates if available, otherwise fall back to filters state
        const rangeStartDate = currentDateType 
            ? new Date(moment.utc(currentDateType.range.GTE).tz(timezone || moment.tz.guess()).format("YYYY-MM-DD") + "T00:00:00")
            : filters.startDate;
        const rangeEndDate = currentDateType 
            ? new Date(moment.utc(currentDateType.range.LTE).tz(timezone || moment.tz.guess()).format("YYYY-MM-DD") + "T00:00:00")
            : filters.endDate;
            
        if (currentType !== "last") {
            newConditions.push({
                range: {
                    fields: ["createdOn"],
                    GTE: moment(moment(rangeStartDate).format("YYYY-MM-DD") + " 00:00:00").utc().format("YYYY-MM-DDTHH:mm:ss.sss") + "Z",
                    LTE: moment(moment(rangeEndDate).format("YYYY-MM-DD") + " 23:59:00").utc().format("YYYY-MM-DDTHH:mm:ss.sss") + "Z"
                },
                conditionOperator: "AND"
            });
        }

        // Condition 3: CreatedBy filter (if selected)
        if (currentCreatedBy) {
            newConditions.push({
                fields: ["createdBy"], terms: [createdBy.id], exact: true,
                conditionOperator: "AND"
            });
        }

        if (!conditions && tags) tableRef?.current?.getData({ newRows: effectiveRows, newSort: currentType === 'last' ? [{ field: "createdOn", order: "DESC" }] : undefined, newConditions, newColumns });
        else tableRef?.current?.getData({ newRows: effectiveRows, newSort: sort || (currentType === 'last' ? [{ field: "createdOn", order: "DESC" }] : undefined), newConditions, newColumns });
    }

    const renderExtraButton = () => {
        return (
            <div style={{ /* flex: '1 1 auto',  */display: 'flex', justifyContent: 'flex-end' }}>
                <Filters
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    setBeforeRangeDate={setBeforeRangeDate}
                    beforeRangeDate={beforeRangeDate}
                    onChangeFilters={() => applyFilters()}
                    last={last}
                    setLast={setLast}
                    type={type}
                    setType={(value) => {
                        setType(value);
                        if (value === 'range') {
                            tableRef?.current?.setRows(RANGE_MODE_ROWS);
                        }
                        applyFilters(value)
                    }}
                    onChangeStartEndDate={onChangeStartEndDate}
                    onCloseSelectDate={onCloseSelectDate}
                    onChange={onChange}
                />
            </div>
        )
    }

    const renderExtraFilters = () => {
        return (
            <div className="extra-filters-tab" style={{ display: 'flex', justifyContent: 'end' }}>
                {
                    <Select
                        id="users"
                        placeholder={messages['activities_tab_username']}
                        options={users}
                        value={createdBy && users.find(x => x.id === createdBy)}
                        textField="userName"
                        valueField="id"
                        onChange={(items = []) => usersHandler(items)}
                        isClearable
                    />
                }

            </div>
        )
    }

    useEffect(() => {
        if (pmUsers.length || users.length) {
            let currentColumns = [...columns]
            let index = currentColumns.findIndex((item) => item.key === "createdBy")
            if (index > -1) {
                currentColumns[index].functionString = `
                    if(row?.platform === "PM"){
                        let pmUsers = ${JSON.stringify(pmUsers)}
                        pmUsers = pmUsers.find((item) => item.username === row?.username)
                        return pmUsers.firstName + " " + pmUsers.lastName || row?.username 
                    } else {
                        let pxUsers = ${JSON.stringify(users)}
                        let regexSubUser = /^PX_\d+_\d+$/;
                        if (regexSubUser.test(row?.createdBy)) {
                            let id = row?.createdBy.split("_")[2];
                            let user = pxUsers.find((item) => (item?.id == +id))
                            return user?.userName
                        } else {
                            return ${companyName ? JSON.stringify(companyName) : ""}
                        }
                    }`
                currentColumns[index].render = (item) => {
                    return <span>{item?.createdBy && item?.platform === "PM" ? getPMUser(item?.createdBy, pmUsers) : accountValidation(item?.createdBy, users)}</span>
                }
            }
            setColumns([...currentColumns])
        }
    }, [pmUsers, users])

    useEffect(() => {
        dispatch(searchEntityRequest({ key: "pmUsers" }));
        if (device.companyId) dispatch(activitiesSearchUsersTabRequest(device.companyId, "companyId", companyName));
        return () => {
            dispatch(clearRedux("ACTIVITIES_TAB", { users: [] }))
        }
    }, [])

    return (
        <div className="troubleshooting-users-tab">
            <DataViewInfiniteScroll
                ref={tableRef}
                columns={columns}
                showTotalCount={type !== 'last'}
                name={`pm-user-troubleshooting-${device.id}`}
                entity={`user-troubleshooting-device-${device.id}`}
                infiniteScroll={type !== 'last'}
                setColumns={(items) => setColumns(items)}
                extraButton={renderExtraButton}
                extraFilters={renderExtraFilters}
                onChangeConditions={handleChangeConditions}
                tableButtons={["export", 'orderColumn']}
                onChangeColumns={(items) => setColumns(items)}
                noPDF
                graphqlConfig={{
                    function: "searchUserTroubleshootings",
                    sortType: "UserTroubleshootingSortInput",
                    conditionsType: "UserTroubleshootingSearchInput",
                    customQuery: "id flow",
                    dynamicQuery: true,
                }}
                searchConfig={{
                    disabled: true,
                }}
                uiConfig={{
                    hideFilters: true,
                    hideSplit: true,
                    hidePagination: type === "last",
                    height: height || 500,
                }}
                selectionConfig={{
                    disabled: true,
                }}
                exportConfig={{
                    fileName: "usertroubleshooting",
                }}
            />
            {open && <RawMessageView />}
        </div>
    );
}

export default UserTroubleshooting;
